/**
 * Frontend Performance Optimizations
 * 
 * This file contains functions and configurations to improve the performance
 * of the SmartBirds application based on Lighthouse audit recommendations.
 */

import React, { lazy, Suspense } from 'react';
import type { ComponentType } from 'react';

/**
 * Lazy load a component with proper Suspense handling
 * @param importFunc - Dynamic import function for the component
 * @param fallbackElement - Optional fallback component/element during loading
 * @returns A lazy-loaded component wrapped in Suspense
 */
export function lazyLoadComponent<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  fallbackElement: React.ReactNode = null
) {
  const LazyComponent = lazy(importFunc);
  
  const WrappedComponent: React.FC<React.ComponentProps<T>> = (props) => {
    return React.createElement(
      Suspense,
      { fallback: fallbackElement },
      React.createElement(LazyComponent, props)
    );
  };
  
  return WrappedComponent;
}

/**
 * Optimized image component that applies best practices
 * - Uses WebP format with fallback
 * - Applies lazy loading with proper priorities
 * - Ensures correct image sizing
 * - Optimizes loading attributes
 */
export function OptimizedImage({
  src,
  alt,
  width,
  height,
  className,
  isPriority = false,
  ...props
}: {
  src: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  isPriority?: boolean;
  [key: string]: any;
}) {
  // Convert to WebP if it's a JPG/PNG from our storage
  const webpSrc = src.includes('storage.googleapis.com') && 
                 (src.endsWith('.jpg') || src.endsWith('.jpeg') || src.endsWith('.png'))
    ? src.substring(0, src.lastIndexOf('.')) + '.webp'
    : src;
  
  // Apply different loading strategies based on priority
  const imgProps = {
    src: webpSrc,
    alt,
    width,
    height,
    className,
    loading: isPriority ? 'eager' : 'lazy',
    fetchPriority: isPriority ? 'high' : 'auto',
    ...props
  };
  
  return React.createElement('img', imgProps);
}

/**
 * Apply a lazy loading strategy to all image elements in a container
 * @param containerRef - Reference to the container element
 */
export function applyLazyLoadingToImages(containerRef: React.RefObject<HTMLElement>) {
  if (!containerRef.current) return;
  
  const images = containerRef.current.querySelectorAll('img:not([loading])');
  
  // Add loading="lazy" to all images that don't have loading attributes
  images.forEach((img: Element) => {
    img.setAttribute('loading', 'lazy');
    
    // Add explicit width/height if missing to prevent layout shifts
    if (!img.getAttribute('width') && !img.getAttribute('height')) {
      // Set initial dimensions based on natural size or default values
      const imgElement = img as HTMLImageElement;
      if (imgElement.naturalWidth && imgElement.naturalHeight) {
        img.setAttribute('width', imgElement.naturalWidth.toString());
        img.setAttribute('height', imgElement.naturalHeight.toString());
      } else {
        img.setAttribute('width', '100%');
        const imgStyle = (img as HTMLElement).style;
        imgStyle.aspectRatio = '16/9';
      }
    }
  });
}

/**
 * Detect and log performance issues
 */
export function monitorPerformance() {
  // Monitor Largest Contentful Paint
  new PerformanceObserver((entryList) => {
    for (const entry of entryList.getEntries()) {
      console.log(`LCP: ${entry.startTime}ms`, entry);
    }
  }).observe({ type: 'largest-contentful-paint', buffered: true });

  // Monitor layout shifts
  new PerformanceObserver((entryList) => {
    for (const entry of entryList.getEntries()) {
      if (!(entry as any).hadRecentInput) {
        console.log(`Layout shift: ${(entry as any).value}`, entry);
      }
    }
  }).observe({ type: 'layout-shift', buffered: true });

  // Monitor long tasks
  new PerformanceObserver((entryList) => {
    for (const entry of entryList.getEntries()) {
      console.log(`Long task: ${entry.duration}ms`, entry);
    }
  }).observe({ type: 'longtask', buffered: true });
}

/**
 * Preconnect to important origins
 * Should be called as early as possible during page load
 */
export function setupResourceHints() {
  const origins = [
    'https://storage.googleapis.com',
    'https://smartbirds-30eb2.firebaseapp.com',
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com'
  ];
  
  origins.forEach(origin => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
}

/**
 * Debounce function to prevent excessive function calls
 * Useful for event handlers that might fire rapidly (scroll, resize, etc.)
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Preload critical resources needed for the application
 * This function should be called early in the application lifecycle
 */
export function preloadCriticalResources() {
  // Set up preconnect resource hints
  setupResourceHints();
  
  // Preload critical CSS if needed
  const preloadCSS = (href: string) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'style';
    link.href = href;
    document.head.appendChild(link);
  };
  
  // Preload critical images
  const preloadImage = (src: string, importance: 'high' | 'low' = 'high') => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    link.setAttribute('importance', importance);
    document.head.appendChild(link);
  };
  
  // Preload the logo
  preloadImage('/assets/logo.webp');
  
  // Preload hero background image
  preloadImage('/mybird.webp');
  
  // Preload critical fonts if needed
  const preloadFont = (href: string) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.href = href;
    link.type = 'font/woff2';
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  };
  
  // Apply DNS prefetch for third-party domains
  const prefetchDNS = (href: string) => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = href;
    document.head.appendChild(link);
  };
  
  prefetchDNS('https://fonts.googleapis.com');
  prefetchDNS('https://storage.googleapis.com');
  
  // Enable early detection of memory issues
  if ('memory' in performance) {
    const memoryInfo = (performance as any).memory;
    if (memoryInfo && memoryInfo.jsHeapSizeLimit) {
      const usage = Math.round((memoryInfo.usedJSHeapSize / memoryInfo.jsHeapSizeLimit) * 100);
      console.log(`Memory usage: ${usage}%`);
    }
  }
}

/**
 * Get responsive image source based on device size
 * @param baseUrl - Base URL of the image
 * @param imagePath - Path to the image
 * @param options - Optional configuration for responsive sizing
 * @returns Object with srcSet and sizes attributes
 */
export function getResponsiveImageSrc(
  baseUrl: string,
  imagePath: string,
  options: {
    widths?: number[];
    sizes?: string;
    format?: 'webp' | 'jpeg' | 'png';
  } = {}
) {
  const { 
    widths = [480, 768, 1024, 1280, 1600], 
    sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
    format = 'webp'
  } = options;

  // If using Cloudflare, would optimize with CF-specific URL parameters
  // For now, just provide the best format
  const baseImageUrl = `${baseUrl}/${imagePath}`;
  
  // Check if we need to convert the format
  let imageUrl = baseImageUrl;
  if (format === 'webp' && !imagePath.endsWith('.webp')) {
    if (imagePath.endsWith('.jpg') || imagePath.endsWith('.jpeg') || imagePath.endsWith('.png')) {
      imageUrl = baseImageUrl.substring(0, baseImageUrl.lastIndexOf('.')) + '.webp';
    }
  }
  
  return {
    src: imageUrl,
    srcSet: widths.map(w => `${imageUrl}?width=${w} ${w}w`).join(', '),
    sizes: sizes
  };
}

/**
 * Generate optimized image attributes for the img tag
 * @param src - Image source URL
 * @param alt - Alt text for the image
 * @param options - Additional options for the image
 * @returns Object with attributes for the img tag
 */
export function generateImageAttributes(
  src: string,
  alt: string,
  options: {
    width?: number | string;
    height?: number | string;
    loading?: 'lazy' | 'eager';
    fetchPriority?: 'high' | 'low' | 'auto';
    decoding?: 'async' | 'sync' | 'auto';
    sizes?: string;
    className?: string;
  } = {}
) {
  const {
    width,
    height,
    loading = 'lazy',
    fetchPriority = 'auto',
    decoding = 'async',
    sizes,
    className
  } = options;
  
  return {
    src,
    alt,
    width,
    height,
    loading,
    fetchPriority,
    decoding,
    sizes,
    className,
    onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
      console.error('Image failed to load:', src);
      e.currentTarget.onerror = null; // Prevent infinite error loop
      e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"%3E%3Cpath fill="%23CCC" d="M21.9 21.9l-8.49-8.49-9.93-9.93L2.1 2.1 0.69 3.51 3 5.83V19c0 1.1 0.9 2 2 2h13.17l2.31 2.31 1.42-1.41zM5 19V7.83l7.17 7.17H5zm11.17-5L5 2.83V5h14v10.17l-2.83-2.83V5h-7.17l2 2H17v7.17z"%3E%3C/path%3E%3C/svg%3E';
    }
  };
}

/**
 * Apply content visibility auto to elements outside the viewport to improve rendering performance
 * @param containerRef - Reference to the container element
 */
export function applyContentVisibilityOptimization(containerRef: React.RefObject<HTMLElement>) {
  if (!containerRef.current) return;
  
  const elements = containerRef.current.querySelectorAll('.optimize-visibility');
  
  elements.forEach((el: Element) => {
    const htmlEl = el as HTMLElement;
    const style = htmlEl.style as CSSStyleDeclaration & {
      contentVisibility: string;
      containIntrinsicSize: string;
    };
    
    // Apply content-visibility: auto to elements
    style.contentVisibility = 'auto';
    
    // Set contain-intrinsic-size to provide a placeholder size
    style.containIntrinsicSize = 'auto 300px';
  });
}
